import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { stringify } from 'qs';
import Config from 'config';
import { IApiaTree, TApiaSystemMessageObj, TFrmParent } from '../types';
import ApiaApi from '../api/apiaApi/apiaApi';

export interface IFieldEventsSlice {
  formSubmitAction: string | null;
  response: TFieldAjaxEventResponsePayload | null;
  isAjaxFieldEventLoading: boolean;
}

export type TFieldAjaxEventParams = {
  ajaxUrl?: string;
  fldId?: string;
  frmId?: string;
  frmParent?: TFrmParent;
  index?: number;
  evtId?: number;
  attId?: string;
};

export type TFieldAjaxEventResponsePayloadPossibleValues = {
  v: string;
  text: string;
};

export type TFieldAjaxEventFields = {
  i: string;
  c?:
    | string
    | {
        valueAttr: {
          v: string;
        };
      };
  e?: {
    f: {
      i: string;
      v: string;
    }[];
  };
  possibleValues?: {
    item: TFieldAjaxEventResponsePayloadPossibleValues[];
  };
  field?: IApiaTree;
};

export type TFieldAjaxEventResponsePayload = {
  success: boolean;
  form: {
    i: string;
    v?: 'f' | 't';
    c?: 'f' | 't';
    field: TFieldAjaxEventFields[];
    changedGrids: { grdId: { id: string } | { id: string }[] };
  }[];
  proccess?: {
    p: string;
  };
  e?: {
    s: string;
  };
  focusTo?: {
    id: string;
    idx: string;
  };
  data?: TApiaSystemMessageObj;
};

export const fetchAjaxFieldEvents = createAsyncThunk<
  TFieldAjaxEventResponsePayload,
  TFieldAjaxEventParams
>(
  'fieldEventsSlice/fetchAjaxFieldEvents',
  async (params: TFieldAjaxEventParams) => {
    const response = await ApiaApi.post<TFieldAjaxEventResponsePayload>(
      params.ajaxUrl ?? Config.FORM_AJAX_FIELD_EVENTS_URL,
      {
        queryData: {
          ...params,
          ...(window.IS_EDIT_GRID_MODAL ? { editionMode: true } : undefined),
        },
        stringifyOptions: { arrayFormat: 'repeat' },
      },
    );
    return response?.data as TFieldAjaxEventResponsePayload;
  },
);

const fieldEventsSlice = createSlice({
  name: 'fieldEventsSlice',
  initialState: {
    formSubmitAction: null,
    response: null,
    isAjaxFieldEventLoading: false,
  } as IFieldEventsSlice,
  reducers: {
    // caall direclty via URLparams or POST api
    setFieldEventSubmitForm: (state, action) => {
      if (state.formSubmitAction === null)
        state.formSubmitAction = `${Config.FORM_SUBMIT_FIELD_EVENTS_URL()}&${stringify(
          action.payload,
          { arrayFormat: 'repeat' },
        )}`;
    },
    reloadCurrentForm(state, action: PayloadAction<string>) {
      state.formSubmitAction = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAjaxFieldEvents.pending, (state, action) => {
      if (action.payload) {
        state.response = action.payload;
        state.isAjaxFieldEventLoading = true;
      }
    });
    builder.addCase(fetchAjaxFieldEvents.fulfilled, (state, action) => {
      if (action.payload) {
        state.formSubmitAction = null;
        state.response = action.payload;
        state.isAjaxFieldEventLoading = false;
      }
    });
    builder.addCase(fetchAjaxFieldEvents.rejected, (state, action) => {
      if (action.payload) {
        state.formSubmitAction = null;
        state.response = null;
        state.isAjaxFieldEventLoading = false;
      }
    });
  },
});
export default fieldEventsSlice.reducer;
export const { reloadCurrentForm, setFieldEventSubmitForm } =
  fieldEventsSlice.actions;
